import { createClient } from '@supabase/supabase-js';
import { config, validateConfig } from '../utils/config';

// Validate configuration on startup
validateConfig();

// Create Supabase client
export const supabase = createClient(
  config.supabaseUrl,
  config.supabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  }
);

// Helper functions for Supabase operations
export const supabaseHelpers = {
  // Get current session
  getCurrentSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    return session;
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('Error getting user:', error);
      return null;
    }
    return user;
  },

  // Sign up with email and password.
  // Throws the raw AuthError so callers can inspect `.code` (e.g. AuthContext
  // for errorCode plumbing). AuthError extends Error, so `.message` is still
  // available for existing message-based mapping.
  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      console.error('Error signing up:', error);
      throw error;
    }

    return data;
  },

  // Sign in with email and password.
  //
  // Note on the weak-password warning surface. In the installed @supabase/
  // auth-js (2.87.x), signInWithPassword returns
  //   { data: { user, session, weakPassword? }, error: null }
  // on a successful sign-in whose password is now below policy (e.g. a
  // grandfathered 6- or 7-character pilot user after Plan 209 raised the
  // minimum to 8). The session is fully issued — error is null. We
  // intentionally discard data.weakPassword: we have no in-app self-serve
  // reset surface to route a user to today, and forced rotation is an
  // orthogonal product call. If a future SDK changes shape to put a
  // WeakPassword-shaped value on `error` instead, revisit this helper.
  // Pinned-in-test by frontend/src/components/Login.passwordPolicy.test.tsx.
  //
  // See signUp comment re: throwing the raw AuthError.
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Error signing in:', error);
      throw error;
    }

    return data;
  },

  // Sign in with Google (implicit flow — tokens arrive in URL fragment on
  // the /auth/callback route and are picked up by detectSessionInUrl).
  signInWithGoogle: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      console.error('Error initiating Google sign-in:', error);
      throw new Error(error.message);
    }

    return data;
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      throw new Error(error.message);
    }
  },

  // Resend a signup confirmation email. Supabase silently no-ops (200 OK) if
  // the account is already confirmed, to avoid leaking confirmation state.
  // No emailRedirectTo — falls back to the project's configured Site URL,
  // which is the correct production behaviour. (Passing a redirectTo would
  // re-open the silent-fallback risk documented in docs/user-authentication.md.)
  resendSignupConfirmation: async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    if (error) {
      console.error('Error resending confirmation:', error);
      throw error;
    }
  },

  // Listen to auth state changes
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  }
};

export default supabase;